import { supabase } from './supabase';
import { toAppError, probablyOffline, AppError } from './errors';
import {
  acknowledge,
  enqueue,
  getBlobsFor,
  markFailure,
  markStatus,
  newMutationId,
  readyMutations,
  countPending,
  type OutboxRecord,
} from './db';

/**
 * Motor de sincronização.
 *
 * Regra dura: a mutação NUNCA sai da fila antes do ACK real do servidor.
 * Toda escrita é idempotente por `mutation_id` — o servidor devolve o
 * resultado anterior em vez de duplicar quando a mesma mutação chega 2×.
 *
 * Ver docs/PERSISTENCIA-OFFLINE.md §3–§6.
 */

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';

interface Handler {
  (rec: OutboxRecord): Promise<{ entity_id?: string }>;
}

const handlers = new Map<string, Handler>();

export function registerHandler(kind: string, fn: Handler): void {
  handlers.set(kind, fn);
}

// ---------------------------------------------------------------- listeners
type Listener = (s: { state: SyncState; pending: number; lastError: string | null }) => void;
const listeners = new Set<Listener>();
let state: SyncState = 'idle';
let lastError: string | null = null;

export function subscribeSync(fn: Listener): () => void {
  listeners.add(fn);
  void notify();
  return () => listeners.delete(fn);
}

async function notify(): Promise<void> {
  const pending = await countPending();
  for (const fn of listeners) fn({ state, pending, lastError });
}

// ---------------------------------------------------------------- enqueue API
export async function submit(args: {
  organizationId: string;
  kind: string;
  entityType: string;
  entityId?: string | null;
  payload: Record<string, unknown>;
  label: string;
  dependsOn?: string | null;
}): Promise<string> {
  const mutationId = newMutationId();
  await enqueue({
    mutation_id: mutationId,
    organization_id: args.organizationId,
    kind: args.kind,
    entity_type: args.entityType,
    entity_id: args.entityId ?? null,
    payload: args.payload,
    depends_on: args.dependsOn ?? null,
    label: args.label,
  });
  await notify();
  // dispara sem bloquear a UI; o "Salvo" só aparece com o ACK
  void flush();
  return mutationId;
}

/** Aguarda o ACK de uma mutação específica (para o feedback "Salvo"). */
export function waitForAck(mutationId: string, timeoutMs = 15_000): Promise<boolean> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = async () => {
      const { getDB } = await import('./db');
      const db = await getDB();
      const rec = await db.get('outbox', mutationId);
      if (!rec) return resolve(true); // saiu da fila = ACK real
      if (rec.status === 'failed' || rec.status === 'conflict') return resolve(false);
      if (Date.now() - started > timeoutMs) return resolve(false); // segue pendente
      setTimeout(() => void tick(), 250);
    };
    void tick();
  });
}

// ---------------------------------------------------------------- flush
let flushing = false;
let flushQueued = false;

export async function flush(organizationId?: string): Promise<void> {
  if (flushing) {
    flushQueued = true;
    return;
  }
  flushing = true;
  try {
    state = 'syncing';
    await notify();

    const batch = await readyMutations(organizationId);
    const done = new Set<string>();

    for (const rec of batch) {
      // respeita dependência (ex.: foto depende da inspeção existir)
      if (rec.depends_on && !done.has(rec.depends_on)) {
        const still = await readyMutations(organizationId);
        if (still.some((m) => m.mutation_id === rec.depends_on)) continue;
      }

      const handler = handlers.get(rec.kind);
      if (!handler) {
        await markFailure(rec.mutation_id, `handler desconhecido: ${rec.kind}`, false);
        continue;
      }

      try {
        await markStatus(rec.mutation_id, 'inflight');
        await handler(rec);
        await acknowledge(rec.mutation_id); // ← só aqui sai da fila
        done.add(rec.mutation_id);
        lastError = null;
      } catch (raw) {
        const err = toAppError(raw);
        await markFailure(rec.mutation_id, err.detail ?? err.message, err.retryable);
        lastError = err.message;
        if (err.kind === 'network') break; // rede caiu: não martela o resto
      }
    }

    const pending = await countPending(organizationId);
    state = pending > 0 ? (probablyOffline() ? 'offline' : 'error') : 'idle';
    if (pending > 0 && lastError === null) state = 'offline';
    await notify();
  } finally {
    flushing = false;
    if (flushQueued) {
      flushQueued = false;
      void flush(organizationId);
    }
  }
}

// ---------------------------------------------------------------- gatilhos
let started = false;

export function startSyncEngine(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  // `online` é gatilho de flush imediato, não prova de conectividade
  window.addEventListener('online', () => void flush());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flush();
  });

  // heartbeat: cobre o caso de o backoff ter vencido sem nenhum evento
  window.setInterval(() => void flush(), 20_000);
  void flush();
}

// ---------------------------------------------------------------- handlers
registerHandler('inspection.start', async (rec) => {
  const { data, error } = await supabase.rpc('app_start_inspection_run', {
    p_campaign_id: rec.payload.campaign_id,
    p_form_template_version_id: rec.payload.form_template_version_id,
    p_asset_id: rec.payload.asset_id ?? null,
    p_target_label: rec.payload.target_label ?? null,
    p_run_date: rec.payload.run_date ?? null,
    p_shift: rec.payload.shift ?? null,
    p_mutation_id: rec.mutation_id,
    // o id vem do aparelho: é o que permite começar a inspeção sem rede e
    // retomá-la depois apontando para a MESMA linha
    p_run_id: rec.payload.run_id ?? null,
  });
  if (error) throw error;
  return { entity_id: data as string };
});

registerHandler('inspection.response', async (rec) => {
  const { data, error } = await supabase.rpc('app_save_response', {
    p_run_id: rec.payload.run_id,
    p_question_id: rec.payload.question_id,
    p_conformity: rec.payload.conformity ?? null,
    p_value_text: rec.payload.value_text ?? null,
    p_value_number: rec.payload.value_number ?? null,
    p_value_date: rec.payload.value_date ?? null,
    p_value_json: rec.payload.value_json ?? null,
    p_note: rec.payload.note ?? null,
    p_mutation_id: rec.mutation_id,
    // mesmo motivo: a foto do item precisa de um destino antes do ACK
    p_response_id: rec.payload.response_id ?? null,
  });
  if (error) throw error;
  return { entity_id: data as string };
});

registerHandler('inspection.finalize', async (rec) => {
  const { error } = await supabase.rpc('app_finalize_inspection_run', {
    p_run_id: rec.payload.run_id,
    p_mutation_id: rec.mutation_id,
  });
  if (error) throw error;
  return {};
});

registerHandler('risk.evaluation.save', async (rec) => {
  const row = rec.payload as Record<string, unknown>;
  const { data, error } = await supabase
    .from('risk_evaluations')
    .upsert({ ...row, mutation_id: rec.mutation_id }, { onConflict: 'mutation_id' })
    .select('id')
    .single();
  if (error) throw error;
  return { entity_id: (data as { id: string }).id };
});

registerHandler('finding.from_risk', async (rec) => {
  const { error } = await supabase.rpc('app_create_finding_from_risk', {
    p_risk_evaluation_id: rec.payload.risk_evaluation_id,
    p_action_type_key: rec.payload.action_type_key ?? null,
    p_due_on: rec.payload.due_on ?? null,
    p_mutation_id: rec.mutation_id,
  });
  if (error) throw error;
  return {};
});

registerHandler('attachment.upload', async (rec) => {
  const blobs = await getBlobsFor(rec.mutation_id);
  const blob = blobs[0];
  if (!blob) throw new AppError('validation', 'Arquivo local não encontrado.', rec.mutation_id);

  const ext = blob.mime === 'image/webp' ? 'webp' : blob.mime === 'image/png' ? 'png' : 'jpg';
  const base = `org/${rec.organization_id}/${rec.payload.entity_type}/${rec.payload.entity_id}`;
  // path derivado do sha256: reenvio é naturalmente idempotente
  const path = `${base}/${blob.sha256}.${ext}`;
  const thumbPath = `${base}/${blob.sha256}_thumb.webp`;

  const up = await supabase.storage
    .from('evidence')
    .upload(path, blob.blob, { contentType: blob.mime, upsert: true });
  if (up.error) throw up.error;

  if (blob.thumbnail) {
    const t = await supabase.storage
      .from('evidence')
      .upload(thumbPath, blob.thumbnail, { contentType: 'image/webp', upsert: true });
    if (t.error) throw t.error;
  }

  const { data, error } = await supabase
    .from('attachments')
    .upsert(
      {
        organization_id: rec.organization_id,
        bucket: 'evidence',
        storage_path: path,
        thumbnail_path: blob.thumbnail ? thumbPath : null,
        original_name: rec.payload.original_name ?? null,
        mime_type: blob.mime,
        bytes: blob.bytes,
        sha256: blob.sha256,
        width: blob.width,
        height: blob.height,
        kind: 'photo',
        mutation_id: rec.mutation_id,
      },
      { onConflict: 'mutation_id' },
    )
    .select('id')
    .single();
  if (error) throw error;

  const attachmentId = (data as { id: string }).id;
  const link = await supabase.from('attachment_links').upsert(
    {
      organization_id: rec.organization_id,
      attachment_id: attachmentId,
      entity_type: rec.payload.entity_type,
      entity_id: rec.payload.entity_id,
      role: rec.payload.role ?? 'evidence',
      // A legenda é escrita junto: quem fotografou no campo sabe o que aquilo
      // é; uma semana depois, ninguém sabe. Só entra no upsert quando vem
      // preenchida — mandar null apagaria uma legenda já editada no lightbox
      // caso a mesma imagem seja reenviada para o mesmo vínculo.
      ...(rec.payload.caption ? { caption: rec.payload.caption as string } : {}),
    },
    { onConflict: 'attachment_id,entity_type,entity_id,role' },
  );
  if (link.error) throw link.error;

  return { entity_id: attachmentId };
});

registerHandler('finding.proposal.review', async (rec) => {
  const { error } = await supabase.rpc('app_revisar_proposta', {
    p_proposta: rec.payload.proposta_id,
    p_acao: rec.payload.acao,
    p_descricao: rec.payload.descricao ?? null,
    p_justificativa: rec.payload.justificativa ?? null,
    p_alvo: rec.payload.alvo ?? null,
    p_mutation_id: rec.mutation_id,
  });
  if (error) throw error;
  return {};
});
